import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CuetPgSidebar = () => (
  <aside className="w-full lg:w-80 xl:w-96 flex-shrink-0">
    <div className="lg:sticky lg:top-28 space-y-6">
      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-accent-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quick Links
        </h3>
        <div className="space-y-2">
          {[
            { label: '🧪 PG Mock Tests', path: '/ugc-net-mock' },
            { label: '📚 Study Materials', path: '/study-materials' },
            { label: '📄 CUET PG PYQs', path: '/pyq-cuet' },
            { label: '🎥 Self-Paced Course', path: '/self-paced' },
            { label: '🎓 PG Counselling Guide', path: '/cuet-pg-counselling' },
            { label: '🌍 Current Affairs', path: '/current-affairs-test' },
          ].map(l => (
            <Link key={l.path} to={l.path}
              className="flex items-center gap-3 p-3 rounded-xl bg-bg-secondary hover:bg-primary/10 transition-colors group">
              <svg className="w-4 h-4 text-text-muted group-hover:text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-text-light group-hover:text-primary transition-colors text-sm">{l.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="glass-card bg-gradient-to-br from-primary/10 to-accent-gold/10">
        <h3 className="text-lg font-bold text-text-light mb-2">CUET PG Counselling</h3>
        <p className="text-text-muted text-sm mb-4">Step-by-step guide to university portals, document checklist, and counselling process.</p>
        <Link to="/cuet-pg-counselling" className="btn-primary block text-center py-3">Read Guide →</Link>
      </div>

      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4">📰 Related Articles</h3>
        <div className="space-y-4">
          {[
            { title: 'CUET PG 2026 Counselling: Step-by-Step Guide', cat: 'Admissions', path: '/cuet-pg-counselling' },
            { title: 'CUET PG Syllabus 2026 — Subject-wise Breakdown', cat: 'Syllabus', path: '/blogs' },
            { title: 'Top Central Universities for CUET PG', cat: 'Universities', path: '/blogs' },
            { title: 'CUET PG vs GATE: Which is Right for You?', cat: 'Guidance', path: '/blogs' },
            { title: 'CUET PG Document Checklist for Counselling', cat: 'Admissions', path: '/cuet-pg-counselling' },
          ].map((a, i) => (
            <Link key={i} to={a.path} className="block group">
              <span className="text-xs text-primary">{a.cat}</span>
              <h4 className="text-text-light group-hover:text-primary transition-colors line-clamp-2 mt-0.5 text-sm">{a.title}</h4>
              {i < 4 && <div className="border-b border-glass-border mt-3" />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </aside>
);

const CuetPgPage = () => (
  <>
    <Helmet>
      <title>CUET PG 2026 – Complete Preparation Guide | BESST</title>
      <meta name="description" content="Prepare for CUET PG 2026 with BESST — mock tests, study materials, counselling guides, and university portal links. Your complete PG entrance companion." />
      <link rel="canonical" href="https://besst.in/cuet-pg" />
    </Helmet>

    <div className="min-h-screen">
      <div className="bg-gradient-to-r from-blue-900/30 via-bg-secondary to-primary/20 pt-32 pb-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="hero-badge mb-4">🎓 Postgraduate Entrance</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-4 leading-tight">CUET (PG) 2026</h1>
          <p className="text-xl text-text-muted max-w-2xl mb-8">
            Your path to India's top postgraduate programs. Prepare strategically with BESST's expert resources and counselling support.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/study-materials" className="btn-primary">📚 Get Study Materials</Link>
            <Link to="/cuet-pg-counselling" className="btn-secondary">Counselling Guide →</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0 space-y-10">

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">1</span>
                What is CUET PG?
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                CUET PG is the national entrance test for postgraduate admissions conducted by NTA. Unlike CUET UG (which has centralised seat allotment), CUET PG is <strong className="text-text-light">decentralised</strong> — each university manages its own counselling, merit lists, and admissions independently based on CUET PG scores.
              </p>
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
                <h4 className="text-primary font-semibold mb-3">📌 Key Facts — CUET PG 2026</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-text-muted text-sm">
                  {[
                    ['Conducting Body', 'NTA'],
                    ['Mode', 'Computer-Based Test'],
                    ['Duration', '105 minutes'],
                    ['Questions', '75 (Domain) + 25 (Language)'],
                    ['Marking', '+4 correct / -1 wrong'],
                    ['Counselling', 'University-wise (decentralised)'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="font-medium text-text-light">{k}:</span><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">2</span>
                Exam Pattern
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-bg-secondary">
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Section</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Questions</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Max Marks</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-text-muted">
                    <tr>
                      <td className="border border-glass-border px-4 py-3 font-medium text-text-light">Domain Knowledge</td>
                      <td className="border border-glass-border px-4 py-3">75</td>
                      <td className="border border-glass-border px-4 py-3">300</td>
                      <td className="border border-glass-border px-4 py-3">105 min</td>
                    </tr>
                    <tr>
                      <td className="border border-glass-border px-4 py-3 font-medium text-text-light">Language (Optional)</td>
                      <td className="border border-glass-border px-4 py-3">25</td>
                      <td className="border border-glass-border px-4 py-3">100</td>
                      <td className="border border-glass-border px-4 py-3">45 min</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">3</span>
                Preparation Strategy
              </h2>
              <div className="space-y-4">
                {[
                  { t: 'Know the Syllabus', d: 'Download your subject-specific syllabus from the NTA website. CUET PG tests advanced domain knowledge — go beyond Class 12 to UG-level concepts.' },
                  { t: 'Practice Previous Year Papers', d: 'PYQs are your best resource. They reveal the exact difficulty level, topic distribution, and question style. Solve at least 5 years of papers.' },
                  { t: 'Mock Tests & Analysis', d: 'Take timed mock tests weekly. Analyse wrong answers — understand why you were wrong, not just what the correct answer is.' },
                  { t: 'University Research', d: 'Since counselling is decentralised, research your target universities early. Know their cut-offs, seat matrices, and registration windows in advance.' },
                ].map((s, i) => (
                  <div key={i} className="glass-card flex gap-4 items-start">
                    <div className="w-8 h-8 flex-shrink-0 bg-primary/20 rounded-full flex items-center justify-center font-bold text-primary text-sm">{i + 1}</div>
                    <div>
                      <h3 className="text-text-light font-bold mb-1">{s.t}</h3>
                      <p className="text-text-muted text-sm">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">4</span>
                Counselling Process
              </h2>
              <p className="text-text-muted mb-4">CUET PG counselling is managed by each university independently. There is no centralised portal — you must register separately on each university's website.</p>
              <Link to="/cuet-pg-counselling" className="glass-card block hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-text-light font-bold">Complete Counselling Guide</h3>
                    <p className="text-text-muted text-sm mt-1">Step-by-step process, document checklist, and direct portal links for DU, BHU, JNU</p>
                  </div>
                  <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </section>

            <div className="bg-gradient-to-r from-primary/20 to-accent-gold/20 border border-primary/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-text-light mb-2">Start Your CUET PG Preparation</h3>
              <p className="text-text-muted mb-4">Access comprehensive study materials, mock tests, and expert counselling tailored for CUET PG 2026.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/subscriptions" className="btn-primary">View Plans →</Link>
                <Link to="/study-materials" className="btn-secondary">Free Study Materials</Link>
              </div>
            </div>
          </article>

          <CuetPgSidebar />
        </div>
      </div>
    </div>
  </>
);

export default CuetPgPage;
